import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiSearch, FiRefreshCw, FiArrowUp, FiDownload,FiCalendar,FiX ,FiEdit2, FiCheck, FiLogOut,FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useNotification } from '../context/NotificationContext';


// Custom calendar button with forwardRef
const CalendarButton = forwardRef(({ value, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    type="button"
    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-600 shadow-sm flex items-center justify-center"
  >
    <FiCalendar className="text-sm" />
  </button>
));

const FullPageLoader = () => (
  <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
    <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const VisitorsPage = () => {
  const [allVisitors, setAllVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Inside the VisitorsPage component, add state to track editable row
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const visitorsPerPage = 10;
  const { notifications, setNotifications } = useNotification();
  console.log("notifications:",notifications);
const [pageLoading, setPageLoading] = useState(false);

const handleFullRefresh = () => {
  setPageLoading(true); // show loader
  setTimeout(() => {
    window.location.reload();
  }, 3000); // small delay so loader is visible
};


// Inside your VisitorsPage component
const navigate = useNavigate();

  // Fetch visitors
  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}visitor/get-all-visitors`
      );
      const result = await response.json();
      console.log("data:",result)
      setAllVisitors(Array.isArray(result.data) ? result.data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, []);


  // Refresh only once per new notification
useEffect(() => {
  if (notifications && notifications.length > 0) {
    const latest = notifications[notifications.length - 1];
    console.log("New notification received:", latest);
    fetchVisitors();
  }
}, [notifications]); 



useEffect(() => {
  let filtered = [...allVisitors]; // clone array

  // Sort by latest first
  filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Filter by selected date
  if (selectedDate) {
    filtered = filtered.filter((v) => {
      if (!v.createdAt) return false;
      const visitDate = new Date(v.createdAt);
      return (
        visitDate.getFullYear() === selectedDate.getFullYear() &&
        visitDate.getMonth() === selectedDate.getMonth() &&
        visitDate.getDate() === selectedDate.getDate()
      );
    });
  }

  // Filter by search term
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (v) =>
        (v.name && v.name.toLowerCase().includes(term)) ||
        (v.mobile && v.mobile.toString().includes(term)) ||
        (v.notes && v.notes.toLowerCase().includes(term))
    );
  }

  setFilteredVisitors(filtered);
  setCurrentPage(1);
}, [selectedDate, searchTerm, allVisitors]);


  // Pagination
  const indexOfLast = currentPage * visitorsPerPage;
  const indexOfFirst = indexOfLast - visitorsPerPage;
  const currentVisitors = filteredVisitors.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredVisitors.length / visitorsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

// Export function
const handleExportExcel = () => {
  const csvRows = [];
  const headers = ["Name", "Mobile", "Visit Date & Time"];
  csvRows.push(headers.join(","));

  allVisitors.forEach((v) => {
    csvRows.push(
      [
        v.name,
        v.mobile,
        v.createdAt
          ? new Date(v.createdAt).toLocaleString("en-US", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "-",
      ].join(",")
    );
  });

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "visitors.csv";
  a.click();
  window.URL.revokeObjectURL(url);
};

const handleUpdateVisitor = async (visitorId) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}visitor/update-visitor`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ _id:visitorId,name: editedName }),
      }
    );

    const result = response.json()
    console.log("data:",result)
    if (!response.ok) throw new Error("Failed to update visitor");

    // Update frontend state
    const updatedVisitors = allVisitors.map((visitor) =>
      visitor._id === visitorId ? { ...visitor, name: editedName } : visitor
    );
    setAllVisitors(updatedVisitors);
    setEditingId(null);
  } catch (err) {
    console.error(err);
    alert("Failed to update visitor name.");
  }
};

  return (
    <div className="p-4 max-w-6xl mx-auto">
{/* Header */}
<div className="flex items-center justify-between mb-4 overflow-x-auto whitespace-nowrap px-1">
  {/* Left side: logo + title */}
  <div className="flex items-center gap-3 flex-shrink-0">
    <img src="/BB.png" alt="Brickbucket" className="h-10 w-auto" />
    <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
      Visitors Dashboard
    </h1>
  </div>

  {/* Right side: Refresh + separator + Logout */}
  <div className="flex items-center gap-3 flex-shrink-0">
    {/* Logout */}
    <button
      onClick={() => {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/visitor");
      }}
      className="p-2 rounded-md hover:bg-gray-100"
      title="Logout"
    >
      <FiLogOut className="w-5 h-5 text-gray-600" />
    </button>
  </div>
</div>




{/* Filters */}
<div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-5 flex flex-wrap gap-3 items-center">
  {/* Search */}
  <div className="relative flex-1 min-w-[200px]">
    <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
    <input
      type="text"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="search mobilenumber"
      className="w-full border border-gray-300 rounded-md pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
    {searchTerm && (
      <button
        onClick={() => setSearchTerm("")}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 hover:text-red-700 p-1 rounded-md"
      >
         <FiX className="w-4 h-4" />
      </button>
    )}
  </div>

</div>



{/* Visitors Table Header */}
<div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg gap-2 sm:gap-0">

  {/* Left: Total visitors (optional) */}
{/*   <div className="flex items-center gap-3 flex-wrap">
    <span className="text-gray-700 font-medium">
      Total Visitors: {filteredVisitors.length}
    </span>
  </div> */}

{/* Center: Pagination + Selected Date */}
<div className="flex items-center gap-3 justify-center overflow-x-auto whitespace-nowrap px-1">
  {/* Pagination */}
  <div className="flex items-center gap-1 flex-shrink-0">
    <button
      onClick={() => handlePageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-1 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
    >
    <FiChevronLeft className="w-5 h-5 font-bold" />
    </button>

    <span className="px-1 py-1 text-sm text-gray-700">
      {currentPage * visitorsPerPage - visitorsPerPage + 1} -{" "}
      {Math.min(currentPage * visitorsPerPage, filteredVisitors.length)} of{" "}
      {filteredVisitors.length}
    </span>

    <button
      onClick={() => handlePageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-1 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
    >
    <FiChevronRight className="w-5 h-5 font-bold" />
    </button>
  </div>

  {/* Selected Date */}
  {selectedDate && (
    <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-sm text-gray-700 flex-shrink-0">
      <span>{selectedDate.toLocaleDateString("en-US")}</span>
      <button
        onClick={() => setSelectedDate(null)}
        className="text-gray-500 hover:text-gray-700"
        title="Clear Date"
      >
        <FiX className="w-4 h-4" />
      </button>
    </div>
  )}
</div>


  {/* Right: Calendar */}
  <div className="flex items-center gap-3">
    <div className="relative flex-shrink-0">
<DatePicker
  selected={selectedDate}
  onChange={(date) => setSelectedDate(date)}
  placeholderText="Select date"
  shouldCloseOnSelect
  withPortal
  customInput={<CalendarButton />}
/>

    </div>
  </div>
</div>





<div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
  <table className="min-w-full text-sm table-auto">
<thead className="bg-gray-50 border-b border-gray-200 sticky top-0 relative">
  <tr>
    <th className="px-4 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
      Mobile
    </th>
    <th className="px-4 py-2 text-center font-semibold text-gray-600 whitespace-nowrap">
      Visit Date
    </th>
  </tr>

  {/* Refresh button absolutely placed inside thead */}
<button
  onClick={handleFullRefresh}
  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100`}
  title="Refresh Page"
>
  <FiRefreshCw className="w-5 h-5 text-gray-600" />
</button>
</thead>
    <tbody>
      {loading ? (
        <tr>
          <td colSpan="3" className="px-4 py-6 text-center">
        <div className="w-6 h-6 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </td>
        </tr>
      ) : currentVisitors.length === 0 ? (
        <tr>
          <td colSpan="3" className="px-4 py-6 text-center text-gray-400">
            No visitors found.
          </td>
        </tr>
      ) : (
        currentVisitors.map((v) => (
        <tr key={v._id} className="odd:bg-white even:bg-gray-100 hover:bg-gray-100 border-b last:border-none">
          {/*             <td className="px-4 py-2 text-gray-800 flex items-center gap-2 whitespace-nowrap">
              {editingId === v._id ? (
                <>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="border border-gray-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button
                    onClick={() => handleUpdateVisitor(v._id)}
                    className="bg-gray-200 hover:bg-gray-300 p-1 rounded-full text-green-600 flex items-center justify-center"
                    title="Save Name"
                  >
                    <FiCheck className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <span>{v.name}</span>
                  <button
                    onClick={() => {
                      setEditingId(v._id);
                      setEditedName(v.name);
                    }}
                    className="bg-gray-200 hover:bg-gray-300 p-1 rounded-full text-gray-600 flex items-center justify-center"
                    title="Edit Name"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </td> */}
            <td className="px-4 py-2 text-gray-800 whitespace-nowrap">{v.mobile}</td>
            <td className="px-4 py-2 text-gray-800 whitespace-nowrap text-center">
              {v.createdAt
                ? `${new Date(v.createdAt).toLocaleDateString("en-US")} ${new Date(
                    v.createdAt
                  ).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}`
                : "-"}
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
</div>



{/* Footer */}
<div className="mt-4 text-center text-gray-500 text-sm">
  Powered by <span className="font-bold">Brickbucket</span>
</div>

{pageLoading && <FullPageLoader />}

    </div>
  );
};

export default VisitorsPage;
